import { TrackingEventList } from 'portal-sdk';
import { alphaSorter } from 'vet-bones/utils';
import { sendHubspotEvent } from 'vet-bones/utils/portal';

import type { DataSourcesAction } from 'src/actions/data-sources/actionCreators';
import { DataSourcesActionType } from 'src/actions/data-sources/actionTypes';
import { UNKNOWN_DATA_SOURCE_TYPE } from 'src/constants/dataSourceTypeDescription';
import { UnknownAction } from 'src/constants/types';
import { buildStardogFailureMessage } from 'src/reducers/notifications';
import {
  connectionAsyncActions,
  connectionsSlice,
} from 'src/store/connections/connections.slice';
import { DataSource, DataSourcesState } from 'src/store/state/dataSources';
import { getDataSourceNameFromUri } from 'src/utils/getDataSourceNameFromUri';
import { getDataSourceTypeFromOptions } from 'src/utils/getDataSourceTypeFromOptions';
import { parseDataSourceTableColumns } from 'src/utils/jsonld/parseDataSourceTableColumns';
import {
  TableEntry,
  TableMetadataJsonLdRow,
  parseDataSourceTableMetadataResults,
  parseDataSourceTablesResults,
} from 'src/utils/jsonld/parseDataSourceTableResults';

// TODO: refactor reducer to fix slice types
type ActionsToFix =
  | UnknownAction<'connections/disconnected'>
  | UnknownAction<'connections/requestValidateConnection/fulfilled'>
  | UnknownAction<'connections/signedOut'>;

export const dataSourcesReducer = (
  prevState: DataSourcesState = new DataSourcesState(),
  action: DataSourcesAction | ActionsToFix
): DataSourcesState => {
  switch (action.type) {
    case connectionsSlice.actions.disconnected.type:
    case connectionsSlice.actions.signedOut.type:
    case connectionAsyncActions.requestValidateConnection.fulfilled.type:
      return {
        ...new DataSourcesState(),
        typeDescriptionByConnectionId: prevState.typeDescriptionByConnectionId,
      };

    case DataSourcesActionType.ADD_DATA_SOURCE_STARTED: {
      return {
        ...prevState,
        pendingAddDataSource: true,
      };
    }

    case DataSourcesActionType.ADD_DATA_SOURCE_FINISHED: {
      return {
        ...prevState,
        pendingAddDataSource: false,
      };
    }

    case DataSourcesActionType.ADD_DATA_SOURCE_SUCCESS: {
      const { dataSource } = action.payload.action;

      // don't store password. Stardog returns this as '****'when options are
      // requested, and we overwrite it for REQUEST_DATA_SOURCE_OPTIONS_SUCCESS,
      // but in case the request fails. This can be an empty string
      if (dataSource?.options?.['jdbc.password'] !== undefined) {
        dataSource.options['jdbc.password'] = '****';
      }

      const dataSourceIds: string[] = [
        ...prevState.dataSourceIds,
        dataSource.id,
      ].sort(alphaSorter);
      const dataSourcesById: Record<string, DataSource> = {
        ...prevState.dataSourcesById,
        [dataSource.id]: dataSource,
      };
      const dataSourceCount = dataSourceIds.length.toString();
      sendHubspotEvent(TrackingEventList.CONNECT_TO_DATASOURCE, {
        data_source_type: dataSource.type,
        application: 'Designer',
        data_source_count: dataSourceCount,
      });
      sendHubspotEvent(TrackingEventList.DESIGNER_CREATE_DATA_SOURCE);
      return {
        ...prevState,
        dataSourceIds,
        dataSourcesById,
      };
    }

    case DataSourcesActionType.REFRESH_DATA_SOURCES_STARTED:
      return {
        ...prevState,
        dataSourceIds: [],
        dataSourcesById: {},
        pendingRefreshDataSources: true,
      };
    case DataSourcesActionType.REFRESH_DATA_SOURCES_FINISHED: {
      return {
        ...prevState,
        pendingRefreshDataSources: false,
      };
    }

    case DataSourcesActionType.REQUEST_DATA_SOURCE_INFO_SUCCESS: {
      const { dataSourceId } = action.payload.action;
      const dataSourceInfo = action.payload.response?.body?.info;
      // Preserve existing options, tables, and error state if they exist
      const existingDataSource = prevState.dataSourcesById?.[dataSourceId];

      const dataSource: DataSource = {
        id: dataSourceId,
        available: dataSourceInfo?.available || false,
        entityName: dataSourceInfo?.entityName || '',
        sharable: dataSourceInfo?.sharable || false,
        type: dataSourceInfo?.type || UNKNOWN_DATA_SOURCE_TYPE,
        options: existingDataSource?.options ?? null,
        tables: existingDataSource?.tables ?? null,
        tableMetadataErrorResponse:
          existingDataSource?.tableMetadataErrorResponse ?? null,
      };

      const dataSourcesById: Record<string, DataSource> = {
        ...prevState.dataSourcesById,
        [dataSourceId]: dataSource,
      };
      const dataSourceIds = Object.keys(dataSourcesById);
      dataSourceIds.sort(alphaSorter);

      return {
        ...prevState,
        dataSourceIds,
        dataSourcesById,
      };
    }

    case DataSourcesActionType.REQUEST_DATA_SOURCE_TABLE_COLUMNS_ATTEMPT: {
      return {
        ...prevState,
        catalogTablesByIri: {},
      };
    }

    case DataSourcesActionType.REQUEST_DATA_SOURCE_TABLE_COLUMNS_SUCCESS: {
      const catalogTablesByIri = parseDataSourceTableColumns(
        action.payload.response?.body?.results?.bindings || []
      );

      return {
        ...prevState,
        catalogTablesByIri,
      };
    }

    case DataSourcesActionType.REQUEST_DATA_SOURCE_TYPE_DESCRIPTION_SUCCESS: {
      const { connection } = action.payload.action;
      const typeDescription =
        action.payload?.response?.body?.data_source_type_description;

      if (!typeDescription) {
        return prevState;
      }

      return {
        ...prevState,
        typeDescriptionByConnectionId: {
          ...prevState.typeDescriptionByConnectionId,
          [connection.id]: typeDescription,
        },
      };
    }

    case DataSourcesActionType.REQUEST_DATA_SOURCE_LIST_INFO_SUCCESS: {
      const dataSourcesById: Record<string, DataSource> = {};
      (action.payload?.response?.body?.data_sources || []).forEach(
        (
          dataSource: Pick<
            DataSource,
            'entityName' | 'sharable' | 'available' | 'type'
          >
        ) => {
          const id = getDataSourceNameFromUri(dataSource.entityName);
          // Preserve existing options, tables, and error state if they exist
          const existingDataSource = prevState.dataSourcesById?.[id];
          dataSourcesById[id] = {
            ...dataSource,
            id,
            type: dataSource.type || UNKNOWN_DATA_SOURCE_TYPE,
            options: existingDataSource?.options ?? null,
            tables: existingDataSource?.tables ?? null,
            tableMetadataErrorResponse:
              existingDataSource?.tableMetadataErrorResponse ?? null,
          };
        }
      );

      const dataSourceIds = Object.keys(dataSourcesById);
      dataSourceIds.sort(alphaSorter);

      return {
        ...prevState,
        dataSourceIds,
        dataSourcesById,
      };
    }

    case DataSourcesActionType.REQUEST_DATA_SOURCE_OPTIONS_SUCCESS: {
      const { connection, dataSourceId } = action.payload.action;
      const options: Record<string, any> =
        action.payload?.response?.body?.options;

      if (!prevState.dataSourcesById?.[dataSourceId]) {
        return prevState;
      }

      const type =
        prevState.dataSourcesById[dataSourceId].type ===
        UNKNOWN_DATA_SOURCE_TYPE
          ? getDataSourceTypeFromOptions(
              options,
              prevState.typeDescriptionByConnectionId[connection.id]
            )
          : prevState.dataSourcesById[dataSourceId].type;

      return {
        ...prevState,
        dataSourcesById: {
          ...prevState.dataSourcesById,
          [dataSourceId]: {
            ...prevState.dataSourcesById[dataSourceId],
            type,
            options,
          } as DataSource,
        },
      };
    }

    case DataSourcesActionType.REQUEST_DATA_SOURCE_OPTIONS_FAILURE: {
      const { dataSourceId } = action.payload.action;

      if (!prevState.dataSourcesById?.[dataSourceId]) {
        return prevState;
      }

      return {
        ...prevState,
        dataSourcesById: {
          ...prevState.dataSourcesById,
          [dataSourceId]: {
            ...prevState.dataSourcesById[dataSourceId],
            options: {},
          } as DataSource,
        },
      };
    }

    // for before Stardog 10.1.0
    case DataSourcesActionType.REQUEST_DATA_SOURCE_TABLE_METADATA_SUCCESS: {
      const { isCatalogSupported } = action.payload?.action;
      const tableMetadataJsonLdRows: TableMetadataJsonLdRow[] =
        action.payload?.response?.body;
      const dataSourcesById = parseDataSourceTableMetadataResults({
        isCatalogSupported,
        prevDataSourcesById: prevState.dataSourcesById,
        tableMetadataJsonLdRows,
      });

      return {
        ...prevState,
        dataSourcesById,
      };
    }
    case DataSourcesActionType.REQUEST_DATA_SOURCE_TABLE_METADATA_FAILURE: {
      const status = action.payload.response?.status;
      const errorMessage = buildStardogFailureMessage(action);

      if (!prevState.dataSourcesById) {
        return prevState;
      }

      const { dataSourceId } = action.payload.action;

      return {
        ...prevState,
        dataSourcesById: {
          ...prevState.dataSourcesById,
          [dataSourceId]: {
            ...(prevState.dataSourcesById[dataSourceId] || {}),
            tableMetadataErrorResponse: { status, errorMessage },
            tables: [],
          },
        },
      };
    }

    // for after Stardog 10.1.0
    case DataSourcesActionType.REQUEST_DATA_SOURCE_TABLES_SUCCESS: {
      const tableEntries = (action.payload.response?.body ||
        []) as TableEntry[];
      const dataSourcesById = parseDataSourceTablesResults({
        dataSourceId: action.payload.action.dataSourceId,
        prevDataSourcesById: prevState.dataSourcesById,
        tableEntries,
      });
      return {
        ...prevState,
        dataSourcesById,
      };
    }
    case DataSourcesActionType.REQUEST_DATA_SOURCE_TABLES_FAILURE: {
      const status = action.payload.response?.status;
      const errorMessage = buildStardogFailureMessage(action);

      if (!prevState.dataSourcesById) {
        return prevState;
      }

      const { dataSourceId } = action.payload.action;

      return {
        ...prevState,
        dataSourcesById: {
          ...prevState.dataSourcesById,
          [dataSourceId]: {
            ...(prevState.dataSourcesById[dataSourceId] || {}),
            tableMetadataErrorResponse: { status, errorMessage },
            tables: [],
          },
        },
      };
    }

    case DataSourcesActionType.REQUEST_VIRTUAL_GRAPHS_ATTEMPT:
      return { ...prevState, virtualGraphsById: {} };

    case DataSourcesActionType.REQUEST_VIRTUAL_GRAPHS_SUCCESS: {
      const virtualGraphs: Array<{
        name: string;
        database: string;
        available: boolean;
        // eslint-disable-next-line camelcase
        data_source: string;
      }> = action.payload.response?.body?.virtual_graphs;

      return {
        ...prevState,
        virtualGraphsById: virtualGraphs
          .map(({ available, data_source: dataSource, database, name }) => ({
            available,
            dataSource,
            database,
            name,
          }))
          .reduce((acc, vg) => ({ ...acc, [vg.name]: vg }), {}),
      };
    }

    default:
      return prevState;
  }
};
